/**
 * woMaterialsProjection.js \u2014 Pure module: project quote line items into Work
 * Order material rows. Consumed by `WOCreateSheets` at WO-create time to seed
 * the new WO's `materials[]` from the selected line items.
 *
 * Spec of record: `context/plans/iter19-batch-plan-v2-backend-contracts.md`
 * \u00a7 B5 (2026-07-26). Wires the PT-40.1 evidence loop \u2014 a created WO with a
 * non-empty `materials[]`, whose rollup in JobCostingTab renders real rows.
 *
 * Contract:
 *   - Pure function; no React, no async, no imports. Deterministic on inputs.
 *   - Array in, array out. Non-array input \u2192 [].
 *   - Never mutates the input array or its elements.
 *   - For each line item, emit:
 *       1. ONE row for the product (the item being installed), keyed by
 *          `line.productId` (falls back to `line.id`, then to a name hash).
 *       2. ONE row PER accessory attached to the line, keyed by
 *          `accessory.accessory_id || accessory.id`.
 *     Pure-labor custom lines (no material cost, no sqft, no accessories)
 *     are skipped.
 *
 * Row shape (each output element):
 *   {
 *     // Consumed by JobCostingTab.rollupMaterials (`rollupMaterials`
 *     // reads `materialId`/`material_id`, `name`/`description`, `unit`,
 *     // `quantity`/`bidQty`, `actualUsed`/`actual_used`):
 *     materialId: string,        // stable merge key
 *     name:       string,        // human label
 *     unit:       string,        // 'sq ft' | 'lin ft' | 'ea' | \u2026
 *     quantity:   number,        // bid quantity (spec: `coverage`; alias also emitted)
 *     actualUsed: null,          // WO edits populate later
 *
 *     // Aliases carrying the exact keys named in the batch plan \u00a7 B5:
 *     coverage:   number,        // === quantity (plan spec name)
 *     used:       null,          // === actualUsed (plan spec name; 0 in plan, but
 *                                //   null preserves "not yet entered" vs "zero"
 *                                //   \u2014 rollupMaterials only sums when != null)
 *
 *     // Provenance for WO materials edit UI + audit trail:
 *     sourceLineId:  string,     // the originating line item id
 *     kind:          'product' | 'accessory',
 *     areaLabel:     string?,
 *     subAreaLabel:  string?,
 *     categoryLabel: string?,
 *
 *     // Cost passthrough (in whatever unit the line stores \u2014 usually cents):
 *     materialCost: number,
 *   }
 *
 * \u26a0 Spec-vs-code note: the batch plan cites row shape
 * `{materialId, name, unit, coverage, used}` and says this "matches
 * JobCostingTab.rollupMaterials contract". `rollupMaterials` actually reads
 * `quantity`/`bidQty` and `actualUsed`/`actual_used` \u2014 not `coverage`/`used`.
 * This module emits BOTH so the plan-cited names stay accessible AND the
 * rollup actually sums. Flag in the B5 completion report; do not silently
 * pick one over the other.
 *
 * @param {Array<object>} lineItems  Committed quote line items.
 * @returns {Array<object>}          Zero or more material rows per line.
 */
export function projectLineItemsToWOMaterials(lineItems) {
  if (!Array.isArray(lineItems)) return [];

  const out = [];
  for (const line of lineItems) {
    if (!line || typeof line !== 'object') continue;

    const provenance = {
      sourceLineId:  line.id || null,
      areaLabel:     line.areaLabel || null,
      subAreaLabel:  line.subAreaLabel || null,
      categoryLabel: line.categoryLabel || null,
    };

    // (1) Product row \u2014 the item itself.
    const productRow = buildProductRow(line, provenance);
    if (productRow) out.push(productRow);

    // (2) One row per accessory.
    const accessories = Array.isArray(line.accessories) ? line.accessories : [];
    for (const a of accessories) {
      const accRow = buildAccessoryRow(a, line, provenance);
      if (accRow) out.push(accRow);
    }
  }
  return out;
}

// ─── internals ───────────────────────────────────────────────────────────────

// Stable-ish fallback id for lines/accessories that lack a persistent id.
// Deliberately short + deterministic; NOT cryptographic. Used only when both
// primary and secondary ids are absent (custom items with no persistence yet).
function nameHash(s) {
  const str = String(s || '');
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  // Format as an unambiguous synthetic id so it's greppable in DDB / logs.
  return `syn-${(h >>> 0).toString(36)}`;
}

function buildProductRow(line, provenance) {
  const materialCost = Number(line.materialCost) || 0;
  const sqft         = line.sqft != null && line.sqft !== ''
    ? Number(line.sqft) || 0
    : 0;

  // Skip pure-labor lines (custom items with no material AND no coverage).
  // Accessories are still emitted for these lines via the caller's loop.
  const hasMaterialToProject = materialCost > 0 || sqft > 0;
  if (!hasMaterialToProject) return null;

  // IDI-816: a job's lines (the quote snapshot) carry `item_name` and no
  // productLabel/categoryLabel; the row's name reads it before "Material".
  // The fallback key below hashes the old label, so materialId is unchanged.
  const keyLabel = line.productLabel || line.categoryLabel || 'Material';
  const name = line.productLabel || line.item_name || keyLabel;
  const materialId =
    line.productId ||
    line.id ||
    nameHash(keyLabel);

  const quantity = sqft;
  return {
    // rollupMaterials contract
    materialId,
    name,
    unit:       line.unit || 'sq ft',
    quantity,
    actualUsed: null,
    // plan-spec aliases (\u00a7 B5)
    coverage:   quantity,
    used:       null,
    // provenance
    kind: 'product',
    ...provenance,
    // costs passthrough
    materialCost,
  };
}

function buildAccessoryRow(a, line, provenance) {
  if (!a || typeof a !== 'object') return null;

  const qty      = Number(a.qty) || 0;
  const unitCost = Number(a.unit_cost) || 0;
  // Emit zero-qty accessories too \u2014 they were attached deliberately by the
  // estimator (may be a placeholder waiting on field measurement).
  const materialCost = qty * unitCost;

  const name = a.name || 'Accessory';
  const materialId =
    a.accessory_id ||
    a.id ||
    nameHash(`${line.id || ''}::${name}`);

  return {
    // rollupMaterials contract
    materialId,
    name,
    unit:       a.unit || 'ea',
    quantity:   qty,
    actualUsed: null,
    // plan-spec aliases (\u00a7 B5)
    coverage:   qty,
    used:       null,
    // provenance
    kind: 'accessory',
    ...provenance,
    // costs passthrough
    materialCost,
  };
}
